'use client';

import { useState, useEffect, type MouseEvent as ReactMouseEvent } from 'react';
import { useTheme } from '@/components/marketing/engine/theme-context';
import { LogoLockup } from '@/components/marketing/hero/logo';

/* ───────────────── Theme Toggle (inline SVG) ───────────────── */

export function ThemeToggle() {
  const { theme, toggle } = useTheme();
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  return (
    <button onClick={toggle} aria-label="Toggle theme" style={{
      width: '34px', height: '34px', borderRadius: 'var(--r-full)',
      border: '1px solid var(--border)', background: 'var(--surface)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      cursor: 'pointer', color: 'var(--text-2)', transition: 'color 0.2s, border-color 0.2s',
    }}
    onMouseEnter={(e: ReactMouseEvent<HTMLButtonElement>) => { e.currentTarget.style.color = 'var(--text)'; e.currentTarget.style.borderColor = 'var(--border-hi)'; }}
    onMouseLeave={(e: ReactMouseEvent<HTMLButtonElement>) => { e.currentTarget.style.color = 'var(--text-2)'; e.currentTarget.style.borderColor = 'var(--border)'; }}
    >
      {!mounted
        ? null
        : theme === 'dark'
        ? <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4"/></svg>
        : <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z"/></svg>}
    </button>
  );
}

/* ───────────────── Navigation ───────────────── */

interface NavItem {
  label: string;
  href: string;
}

const NAV_ITEMS: NavItem[] = [
  { label: 'Platform', href: '#command' },
  { label: 'How it works', href: '#infra' },
  { label: 'FAQ', href: '#faq' },
];

export function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let t = false;
    const h = () => { if (t) return; t = true; requestAnimationFrame(() => { setScrolled(window.scrollY > 36); t = false; }); };
    window.addEventListener('scroll', h, { passive: true });
    return () => window.removeEventListener('scroll', h);
  }, []);

  const go = (href: string) => {
    const el = document.querySelector(href);
    if (el) window.scrollTo({ top: (el as HTMLElement).offsetTop - 70, behavior: 'smooth' });
    setOpen(false);
  };

  return (
    <>
      <nav style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100, height: '64px',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 24px',
        background: scrolled ? 'color-mix(in srgb, var(--bg) 80%, transparent)' : 'transparent',
        backdropFilter: scrolled ? 'blur(20px) saturate(1.4)' : 'none',
        WebkitBackdropFilter: scrolled ? 'blur(20px) saturate(1.4)' : 'none',
        borderBottom: `1px solid ${scrolled ? 'var(--border)' : 'transparent'}`,
        boxShadow: scrolled ? 'none' : '0 1px 60px -14px color-mix(in srgb, var(--indigo) calc(var(--sing-glow, 0.55) * 55%), transparent)',
        transition: 'background .4s, border-color .4s, backdrop-filter .4s, box-shadow .4s',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', maxWidth: '1200px' }}>
          {/* Logo → vhire.com home (the candidate/job-seeker site). This is the
              primary "get back to vhire.com" affordance from the /employer page;
              previously it only scrolled to top, leaving no way out. */}
          <a href="/" aria-label="VHire home"
            style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', color: 'var(--text)' }}>
            <LogoLockup height={24} />
          </a>

          <div className="nav-center" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            {NAV_ITEMS.map(it => (
              <button key={it.label} onClick={() => go(it.href)} style={{
                padding: '7px 16px', borderRadius: 'var(--r-full)', fontSize: '13.5px', fontWeight: 500,
                color: 'var(--text-2)', background: 'none', border: 'none', cursor: 'pointer', transition: 'color .2s',
              }}
              onMouseEnter={(e: ReactMouseEvent<HTMLButtonElement>) => { e.currentTarget.style.color = 'var(--text)'; }}
              onMouseLeave={(e: ReactMouseEvent<HTMLButtonElement>) => { e.currentTarget.style.color = 'var(--text-2)'; }}>{it.label}</button>
            ))}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <ThemeToggle />
            {/* Two distinct doors. "Sign in / Sign up" was one link to /login
                for both, which only worked while signup was self-serve —
                new users now request access and wait for a grant, so
                collapsing the two would mislabel half the traffic. */}
            <a href="/login" style={{
              padding: '9px 22px', borderRadius: 'var(--r-full)', border: '1px solid var(--border-hi)',
              background: 'var(--surface)', color: 'var(--text)', fontSize: '13.5px', fontWeight: 600, cursor: 'pointer',
              textDecoration: 'none', display: 'inline-flex', alignItems: 'center',
              transition: 'background .2s, border-color .2s, transform .2s',
            }}
            onMouseEnter={(e: ReactMouseEvent<HTMLAnchorElement>) => { e.currentTarget.style.borderColor = 'var(--text-3)'; e.currentTarget.style.background = 'var(--surface-raised)'; }}
            onMouseLeave={(e: ReactMouseEvent<HTMLAnchorElement>) => { e.currentTarget.style.borderColor = 'var(--border-hi)'; e.currentTarget.style.background = 'var(--surface)'; }}>
              Sign in
            </a>
            <a className="nav-cta" href="/request-access" style={{
              padding: '9px 22px', borderRadius: 'var(--r-full)', border: '1px solid transparent',
              background: 'var(--indigo)', color: '#fff', fontSize: '13.5px', fontWeight: 600, cursor: 'pointer',
              textDecoration: 'none', display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap',
              transition: 'filter .2s, transform .2s',
            }}
            onMouseEnter={(e: ReactMouseEvent<HTMLAnchorElement>) => { e.currentTarget.style.filter = 'brightness(1.1)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
            onMouseLeave={(e: ReactMouseEvent<HTMLAnchorElement>) => { e.currentTarget.style.filter = 'none'; e.currentTarget.style.transform = 'none'; }}>
              Request access
            </a>
            <button className="nav-burger" onClick={() => setOpen(!open)} style={{
              display: 'none', width: '36px', height: '36px', alignItems: 'center', justifyContent: 'center',
              background: 'none', border: 'none', color: 'var(--text)', cursor: 'pointer',
            }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                {open ? <><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></> : <><line x1="4" y1="7" x2="20" y2="7"/><line x1="4" y1="12" x2="20" y2="12"/><line x1="4" y1="17" x2="20" y2="17"/></>}
              </svg>
            </button>
          </div>
        </div>
      </nav>

      {open && (
        <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 99, paddingTop: '64px',
          background: 'color-mix(in srgb, var(--bg) 96%, transparent)', backdropFilter: 'blur(24px)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '18px' }}>
          {NAV_ITEMS.map(it => (
            <button key={it.label} onClick={(e: ReactMouseEvent<HTMLButtonElement>) => { e.stopPropagation(); go(it.href); }}
              style={{ fontSize: '18px', fontWeight: 500, color: 'var(--text-2)', background: 'none', border: 'none', cursor: 'pointer', padding: '10px' }}>{it.label}</button>
          ))}
          <a href="/login" onClick={(e: ReactMouseEvent<HTMLAnchorElement>) => { e.stopPropagation(); setOpen(false); }}
            style={{ marginTop: '8px', padding: '11px 28px', borderRadius: 'var(--r-full)', border: '1px solid var(--border-hi)', background: 'var(--surface)', color: 'var(--text)', fontSize: '15px', fontWeight: 600, cursor: 'pointer', textDecoration: 'none' }}>Sign in</a>
          <a href="/request-access" onClick={(e: ReactMouseEvent<HTMLAnchorElement>) => { e.stopPropagation(); setOpen(false); }}
            style={{ padding: '11px 28px', borderRadius: 'var(--r-full)', border: '1px solid transparent', background: 'var(--indigo)', color: '#fff', fontSize: '15px', fontWeight: 600, cursor: 'pointer', textDecoration: 'none' }}>Request access</a>
        </div>
      )}
    </>
  );
}
